import React, { useState, useEffect } from 'react';
import { Box, TableContainer, Table, TableBody, TableCell, TableRow, Card } from '@mui/material';
import { Square, Devices, Apps } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import ContactsTable from '../ContactsTable';
import renderPercentage from '../renderPercentage';
import generateChartColours from '../../generateChartColours';
import useApi from '../../hooks/useApi';
import ReportsCardHeader from '../cards/ReportsCardHeader';
import ReportsCardContent from '../cards/ReportsCardContent';
import DevicesChart from '../charts/DevicesChart';

const Item = ({ title, data, selectedItem, onSelectItem, icon }) => {
    const totalContacts = data.reduce((acc, { total }) => (acc + total), 0);

    const colours = generateChartColours(data);

    const chartData = data.map(e => ({ value: e.total, name: e.label }));

    return (
        <Card>
            <ReportsCardHeader title={title} icon={icon} />
            <ReportsCardContent paddingBottom={2}>
                <Box sx={{ display: 'flex' }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    {data.map(({ label, total }, i) => (
                                        <TableRow
                                            key={label}
                                            hover
                                            onClick={() => onSelectItem(label)}
                                            selected={selectedItem === label}
                                        >
                                            <TableCell width="52">
                                                <Square fontSize="small" sx={{ color: colours[i], display: 'block' }} />
                                            </TableCell>
                                            <TableCell component="th">{label}</TableCell>
                                            <TableCell width="100" align="right">{total}</TableCell>
                                            <TableCell width="100" align="right">{renderPercentage(total, totalContacts)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    <Box sx={{ width: 300 }}>
                        <DevicesChart
                            label={title}
                            data={chartData}
                            height={150}
                            colours={colours}
                            showLegend={false}
                        />
                    </Box>
                </Box>
            </ReportsCardContent>
        </Card>
    );
};

const NewDevice = ({ id, onRefreshGroups }) => {
    const [devices, setDevices] = useState(null);
    const [clients, setClients] = useState(null);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { handleGet } = useApi();

    const handleFetchDevices = async () => {
        const response = await handleGet(`reports/${id}/devices`);

        if (!response.ok) {
            return;
        }

        const data = await response.json();
        setDevices(data);
    };

    const handleFetchClients = async () => {
        const response = await handleGet(`reports/${id}/clients`);

        if (!response.ok) {
            return;
        }

        const data = await response.json();
        setClients(data);
    };

    const handleSelectDevice = device => {
        setSelectedDevice(device);
        setSelectedClient(null);
    };

    const handleSelectClient = client => {
        setSelectedClient(client);
        setSelectedDevice(null);
    };

    useEffect(() => {
        handleFetchDevices();
        handleFetchClients();
    }, []);

    useEffect(() => {
        (clients && devices) && setIsLoading(false);
    }, [clients, devices]);

    if (isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <Box sx={{ margin: 2 }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Item
                        title="Devices"
                        data={devices}
                        selectedItem={selectedDevice}
                        onSelectItem={handleSelectDevice}
                        icon={<Devices />}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Item
                        title="Mail Apps"
                        data={clients}
                        selectedItem={selectedClient}
                        onSelectItem={handleSelectClient}
                        icon={<Apps />}
                    />
                </Grid>
                {(selectedDevice || selectedClient) && (
                    <Grid size={12}>
                        <ContactsTable
                            campaignId={id}
                            device={selectedDevice}
                            client={selectedClient}
                            enableJumping={true}
                            onRefreshGroups={onRefreshGroups}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default NewDevice;